import Vue from "vue";
import Router, { Route } from "vue-router";
import DashboardPage from "./views/DashboardPage.vue";
import store from "./store";
import { getServicePermissions } from "@/api/service-api";
import { getProjectPermissions } from "@/api/project-api";
import { getServiceProjectResponsibleStaffs } from "./sales/api/sales-report-api";
import SimplePage from "@/views/SimplePage.vue";

Vue.use(Router);

function signOut(_to: Route, _from: Route, next: Function) {
  store.dispatch("signOut");
  next("/signin");
}

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/",
      name: "dashboard",
      component: DashboardPage
    },
    {
      path: "/accounting",
      component: () =>
        import(/* webpackChunkName: "accounting" */
        "./ui/accounting/pages/AccountingPage.vue"),
      children: [
        {
          path: "/estimates",
          name: "estimate-list",
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./ui/accounting/pages/AccountingEstimate.vue")
        },
        {
          path: "invoices",
          name: "invoice-list",
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./ui/accounting/pages/AccountingInvoice.vue")
        },

        {
          path: "purchases",
          name: "purchase-list",
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./ui/accounting/pages/AccountingPurchase.vue")
        },

        {
          path: "sales-report",
          name: "sales-report",
          props: true,
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./sales/ui/pages/SalesReportAllProjectPage.vue"),
          meta: {
            servicePermission: "service/master_read"
          }
        },
        {
          path: "expenses-report",
          name: "expenses-report",
          props: true,
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./sales/ui/pages/ExpensesReportPage.vue")
        },
        {
          path: "invoice_management",
          name: "invoice_management",
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./ui/accounting/pages/InvoiceManagementListPage.vue")
        },
        {
          path: "purchases_management",
          name: "purchases_management",
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./ui/accounting/pages/PurchaseManagementListPage.vue")
        },
        {
          path: "",
          redirect: "sales-report"
        }
      ]
    },
    {
      path: "/settings",
      component: () =>
        import(/* webpackChunkName: "settings" */ "./views/UserSettings.vue")
    },
    {
      path: "/projects",
      name: "project-list",
      component: () =>
        import(/* webpackChunkName: "project-list" */
        "./views/ProjectListPage.vue"),
      meta: { servicePermission: "service/project_read" }
    },
    {
      path: "/projects/new",
      name: "project-new",
      component: () =>
        import(/* webpackChunkName: "project-list" */
        "./views/ProjectNewPage.vue"),
      meta: { servicePermission: "service/project_read" }
    },
    {
      path: "/projects/:projectId",
      component: () =>
        import(/* webpackChunkName: "project" */ "./views/ProjectPage.vue"),
      meta: {
        servicePermission: "service/project_read",
        projectPermission: "project/project_read",
        accessProject: "project/project_access"
      },
      props: true,
      children: [
        {
          path: "messages",
          name: "project-show",
          props: true,
          component: () =>
            import(/* webpackChunkName: "project" */ "./views/ProjectChatPage.vue"),
          meta: {
            servicePermission: "service/project_read",
            projectPermission: "project/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          path: "tasks",
          name: "project-tasks",
          props: true,
          component: () =>
            import(/* webpackChunkName: "project" */ "./views/ProjectChartPage.vue"),
          meta: {
            servicePermission: "service/project_read",
            projectPermission: "project/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          path: "reports",
          name: "report-list",
          props: true,
          component: () =>
            import(/* webpackChunkName: "report" */
            "./views/ReportListPage.vue"),
          meta: {
            servicePermission: "service/project_read",
            projectPermission: "project/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          // 個別に取るAPIがないのでフォルダが必須
          path: "reports/:folderId/new",
          name: "report-new",
          props: true,
          component: () =>
            import(/* webpackChunkName: "report" */
            "./views/ReportNewPage.vue"),
          meta: {
            servicePermission: "service/project_operation",
            projectPermission: "project/document_secret",
            accessProject: "project/project_access"
          }
        },
        {
          // 個別に取るAPIがないのでフォルダが必須
          path: "reports/:folderId/:reportId",
          name: "report-show",
          props: true,
          component: () =>
            import(/* webpackChunkName: "report" */
            "./views/ReportShowPage.vue"),
          meta: {
            servicePermission: "service/project_read",
            projectPermission: "project/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          // 個別に取るAPIがないのでフォルダが必須
          path: "reports/:folderId/:reportId/edit",
          name: "report-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "report" */
            "./views/ReportEditPage.vue"),
          meta: {
            servicePermission: "service/project_operation",
            projectPermission: "project/document_secret",
            accessProject: "project/project_access"
          }
        },
        {
          path: "minutes",
          name: "minute-list",
          props: true,
          component: () =>
            import(/* webpackChunkName: "minute" */
            "./views/MinuteListPage.vue"),
          meta: {
            servicePermission: "service/project_read",
            projectPermission: "project/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          path: "minutes/:answerId",
          name: "minute-show",
          props: true,
          component: () =>
            import(/* webpackChunkName: "minute" */
            "./views/MinuteShowPage.vue"),
          meta: {
            servicePermission: "service/project_read",
            projectPermission: "project/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          path: "minutes/:answerId/edit",
          name: "minute-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "minute" */
            "./views/MinuteEditPage.vue"),
          meta: {
            servicePermission: "service/master_operation",
            projectPermission: "project/minutes_operation",
            accessProject: "project/project_access"
          }
        },
        {
          path: "photos",
          name: "project-photos",
          props: true,
          component: () =>
            import(/* webpackChunkName: "project" */ "./views/ProjectPhotoPage.vue"),
          meta: {
            servicePermission: "service/project_read",
            projectPermission: "project/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          path: "documents",
          name: "project-documents",
          props: true,
          component: () =>
            import(/* webpackChunkName: "project" */ "./views/ProjectDocumentPage.vue"),
          meta: {
            servicePermission: "service/project_read",
            projectPermission: "project/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          path: "details",
          name: "project-details",
          props: true,
          component: () =>
            import(/* webpackChunkName: "project" */ "./views/ProjectDetailsPage.vue"),
          meta: {
            servicePermission: "service/project_read",
            projectPermission: "project/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          path: "details/edit",
          name: "project-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "project" */ "./views/ProjectEditPage.vue"),
          meta: {
            servicePermission: "service/project_read",
            projectPermission: "project/document_secret",
            accessProject: "project/project_access"
          }
        },
        {
          path: "estimates",
          name: "estimates",
          props: route => ({
            ...route.params,
            editon: true
          }),
          // props: true,
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./ui/accounting/pages/EstimateListPage.vue"),
          // "./ui/accounting/pages/OrderListPage.vue"
          meta: {
            servicePermission: "service/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          path: "estimates/:id",
          name: "estimate-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./ui/accounting/pages/EditEstimatePage.vue"),
          meta: {
            servicePermission: "service/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          path: "estimates/:estimateId/purchase",
          name: "estimate-purchase",
          props: true,
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./ui/accounting/pages/NewPurchasePage.vue"),
          meta: {
            servicePermission: "service/project_read",
            accessProject: "project/project_access"
          }
        },
        // {
        //   path: "estimates/:estimateId/expenses",
        //   name: "estimate-expenses",
        //   props: true,
        //   component: () =>
        //     import(
        //       /* webpackChunkName: "accounting" */
        //       "./ui/accounting/pages/EditExpensesPage.vue"
        //     )
        // },
        // {
        //   path: "estimates/:estimateId/expenses",
        //   name: "estimate-expenses",
        //   props: true,
        //   component: () =>
        //     import(
        //       /* webpackChunkName: "accounting" */
        //       "./ui/accounting/pages/EditExpensesPage.vue"
        //     )
        // },
        {
          path: "invoices",
          name: "invoice",
          props: route => ({
            ...route.params,
            editon: true
          }),
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./ui/accounting/pages/InvoiceListPage.vue"),
          meta: {
            servicePermission: "service/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          path: "invoices/:id",
          name: "invoice-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./ui/accounting/pages/EditInvoicePage.vue"),
          meta: {
            servicePermission: "service/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          path: "purchases",
          name: "purchase",
          props: route => ({
            ...route.params,
            editon: true
          }),
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./ui/accounting/pages/PurchaseListPage.vue"),
          meta: {
            servicePermission: "service/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          path: "purchases/:id",
          name: "purchase-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./ui/accounting/pages/EditPurchasePage.vue"),
          meta: {
            servicePermission: "service/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          path: "expenses",
          name: "expenses",
          props: true,
          component: () =>
            import(/* webpackChunkName: "cost" */
            "./cost/ui/views/ExpenseListPage.vue"),
          meta: {
            servicePermission: "service/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          path: "expenses/:expenseId/edit",
          name: "expenses-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "cost" */
            "./cost/ui/views/ExpenseEditPage.vue"),
          meta: {
            servicePermission: "service/project_read",
            accessProject: "project/project_access"
          }
        },
        {
          path: "sales-report",
          name: "report",
          props: true,
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./sales/ui/pages/SalesReportPage.vue"),
          meta: {
            servicePermission: "service/project_operation",
            projectPermission: "project/project_operation",
            accessProject: "project/project_access"
          }
        },
        {
          path: "orders/sales-report",
          name: "order-sale-report",
          props: true,
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./sales/ui/pages/SalesReportProjectPage.vue"),
          meta: {
            servicePermission: "service/project_operation",
            projectPermission: "project/project_operation",
            accessProject: "project/project_access"
          }
        },
        {
          path: "expenses-report",
          name: "expenses-report",
          props: true,
          component: () =>
            import(/* webpackChunkName: "accounting" */
            "./sales/ui/pages/ExpensesReportPage.vue"),
          meta: {
            servicePermission: "service/project_operation",
            projectPermission: "project/project_operation",
            accessProject: "project/project_access"
          }
        },
        {
          path: "",
          redirect: "messages"
        },
        {
          path: "orders",
          component: SimplePage,
          children: [
            {
              path: "estimates",
              name: "order-estimates",
              props: route => ({
                ...route.params,
                edition: true
              }),
              component: () =>
                import(/* webpackChunkName: "order" */
                "./ui/order/pages/EstimateListPage.vue"),
              meta: {
                servicePermission: "service/project_read",
                accessProject: "project/project_access"
              }
            },
            {
              path: "estimates/:id",
              name: "order-estimate-edit",
              props: true,
              component: () =>
                import(/* webpackChunkName: "order" */
                "./ui/order/pages/EditEstimatePage.vue"),
              meta: {
                servicePermission: "service/project_read",
                accessProject: "project/project_access"
              }
            },
            {
              path: "estimates_actual/:id/",
              name: "order-estimate-actual",
              props: route => ({
                ...route.params
              }),
              component: () =>
                import(/* webpackChunkName: "order" */
                "./ui/order/pages/EditEstimatePage.vue"),
              meta: {
                servicePermission: "service/project_read",
                accessProject: "project/project_access"
              }
            },
            {
              path: "estimates/:estimateId/purchase",
              name: "order-estimate-purchase",
              props: true,
              component: () =>
                import(/* webpackChunkName: "order" */
                "./ui/order/pages/NewPurchasePage.vue"),
              meta: {
                servicePermission: "service/project_read",
                accessProject: "project/project_access"
              }
            },
            {
              path: "estimates_actual/:estimateId/purchase",
              name: "order-estimate-purchase-actual",
              props: true,
              component: () =>
                import(/* webpackChunkName: "order" */
                "./ui/order/pages/NewPurchasePage.vue"),
              meta: {
                servicePermission: "service/project_read",
                accessProject: "project/project_access"
              }
            },
            {
              path: "invoices",
              name: "order-invoice",
              props: route => ({
                ...route.params,
                edition: true
              }),
              component: () =>
                import(
                  /* webpackChunkName: "order" */
                  "./ui/order/pages/InvoiceListPage.vue"
                ),
              meta: {
                servicePermission: "service/project_read",
                accessProject: "project/project_access"
              }
            },
            {
              path: "invoices/:id",
              name: "order-invoice-edit",
              props: true,
              component: () =>
                import(
                  /* webpackChunkName: "order" */
                  "./ui/order/pages/EditInvoicePage.vue"
                ),
              meta: {
                servicePermission: "service/project_read",
                accessProject: "project/project_access"
              }
            },
            {
              path: "purchases",
              name: "order-purchase",
              props: route => ({
                ...route.params,
                edition: true
              }),
              component: () =>
                import(
                  /* webpackChunkName: "order" */
                  "./ui/order/pages/PurchaseListPage.vue"
                ),
              meta: {
                servicePermission: "service/project_read",
                accessProject: "project/project_access"
              }
            },
            {
              path: "purchases/:id",
              name: "order-purchase-edit",
              props: true,
              component: () =>
                import(
                  /* webpackChunkName: "order" */
                  "./ui/order/pages/EditPurchasePage.vue"
                ),
              meta: {
                servicePermission: "service/project_read",
                accessProject: "project/project_access"
              }
            },
            {
              path: "expenses",
              name: "order-expenses",
              props: true,
              component: () =>
                import(
                  /* webpackChunkName: "order" */
                  "./cost/ui/views/ExpenseListPage.vue"
                ),
              meta: {
                servicePermission: "service/project_read",
                accessProject: "project/project_access"
              }
            },
            {
              path: "expenses/:expenseId/edit",
              name: "order-expenses-edit",
              props: true,
              component: () =>
                import(
                  /* webpackChunkName: "order" */
                  "./cost/ui/views/ExpenseEditPage.vue"
                ),
              meta: {
                servicePermission: "service/project_read",
                accessProject: "project/project_access"
              }
            },
            {
              path: "sales-report",
              name: "order-report",
              props: true,
              component: () =>
                import(
                  /* webpackChunkName: "order" */
                  "./sales/ui/pages/SalesReportPage.vue"
                ),
              meta: {
                servicePermission: "service/project_operation",
                projectPermission: "project/project_operation"
              }
            },
            {
              path: "expenses-report",
              name: "order-expenses-report",
              props: true,
              component: () =>
                import(
                  /* webpackChunkName: "order" */
                  "./sales/ui/pages/ExpensesReportPage.vue"
                ),
              meta: {
                servicePermission: "service/project_operation",
                projectPermission: "project/project_operation"
              }
            },
            {
              path: "",
              redirect: "estimates"
            },
          ]
        },
      ]
    },
    {
      path: "/services/select",
      component: () =>
        import(/* webpackChunkName: "admin" */ "./views/SelectService.vue")
    },
    {
      path: "/tasks",
      name: "all-tasks",
      component: () =>
        import(/* webpackChunkName: "task" */
        "./task/ui/pages/TaskPage.vue")
    },
    {
      path: "/admin",
      component: () =>
        import(/* webpackChunkName: "admin" */ "./views/AdminPage.vue"),
      meta: { servicePermission: "service/master_operation" },
      children: [
        {
          path: "project/property/list",
          name: "project-property-list",
          component: () =>
            import(/* webpackChunkName: "project-property-list" */
            "./views/project/property/ProjectPropertyList.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "project/property/keys",
          name: "project-property-keys",
          component: () =>
            import(/* webpackChunkName: "project-property-keys" */
            "./views/project/property/ProjectPropertyKeyListPage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "project/property/keys/create",
          name: "project-property-keys-new",
          component: () =>
            import(/* webpackChunkName: "project-property-keys" */
            "./views/project/property/ProjectPropertyKeyCreatePage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "project/property/keys/:id/edit",
          name: "project-property-keys-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "project-property-keys" */
            "./views/project/property/ProjectPropertyKeyEditPage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "project/folder-default/:type",
          name: "project-folder-default-list",
          component: () =>
            import(/* webpackChunkName: "project-folder-default-list" */
            "./views/project/property/ProjectFolderDefaultListPage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "project/folder-default/create",
          name: "project-folder-default-create",
          component: () =>
            import(/* webpackChunkName: "project-folder-default" */
            "./views/project/property/ProjectFolderDefaultCreatePage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "project/folder-default/:id/edit",
          name: "project-folder-default-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "project-folder-default" */
            "./views/project/property/ProjectFolderDefaultCreatePage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "project/task-default",
          name: "project-task-default",
          props: true,
          component: () =>
            import(/* webpackChunkName: "project-task-default" */
            "./views/project/property/task-default/Index.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "project/construction-type",
          name: "project-construction-type",
          props: true,
          component: () =>
            import(/* webpackChunkName: "project-construction-type" */
            "./views/project/property/construction-type/index.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "project/construction-type/create",
          name: "project-construction-type-create",
          props: true,
          component: () =>
            import(/* webpackChunkName: "project-construction-type-create" */
            "./components/project/property/ConstructionTypeForm.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "project/construction-type/:id/edit",
          name: "project-construction-type-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "project-construction-type-create" */
            "./components/project/property/ConstructionTypeForm.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "project/task-default/create",
          name: "project-task-default-create",
          props: true,
          component: () =>
            import(/* webpackChunkName: "project-task-default" */
            "./views/project/property/task-default/Form.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "project/task-default/:id/edit",
          name: "project-task-default-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "project-task-default" */
            "./views/project/property/task-default/Form.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "companies",
          component: SimplePage,
          meta: { servicePermission: "service/master_operation" },
          children: [
            {
              path: "",
              name: "companies-list",
              props: true,
              component: () =>
                import(/* webpackChunkName: "company" */
                "./views/company/CompanyListPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: "new",
              name: "companies-new",
              component: () =>
                import(/* webpackChunkName: "company" */
                "./views/company/CompanyNewPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":id",
              name: "companies-show",
              props: true,
              component: () =>
                import(/* webpackChunkName: "company" */
                "./views/company/CompanyShowPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":id/edit",
              name: "companies-edit",
              props: true,
              component: () =>
                import(/* webpackChunkName: "company" */
                "./views/company/CompanyEditPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":companyId/staffs/new",
              name: "company-staffs-new",
              props: true,
              component: () =>
                import(/* webpackChunkName: "company" */
                "./views/company/CompanyStaffNewPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":companyId/staffs/:id/edit",
              name: "company-staffs-edit",
              props: true,
              component: () =>
                import(/* webpackChunkName: "company" */
                "./views/company/CompanyStaffEditPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            }
          ]
        },
        {
          path: "enduser",
          component: SimplePage,
          meta: { servicePermission: "service/master_operation" },
          children: [
            {
              path: "",
              name: "enduser-list",
              props: route => ({
                ...route.params,
                companyType: "enduser"
              }),
              component: () =>
                import(/* webpackChunkName: "company" */
                "./views/enduser/EndUserListPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: "new",
              name: "enduser-new",
              props: route => ({
                ...route.params,
                companyType: "enduser"
              }),
              component: () =>
                import(/* webpackChunkName: "company" */
                "./views/enduser/EndUserNewPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":id",
              name: "enduser-show",
              props: route => ({
                ...route.params,
                companyType: "enduser"
              }),
              component: () =>
                import(/* webpackChunkName: "company" */
                "./views/enduser/EndUserShowPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":id/edit",
              name: "enduser-edit",
              props: route => ({
                ...route.params,
                companyType: "enduser"
              }),
              component: () =>
                import(/* webpackChunkName: "company" */
                "./views/enduser/EndUserEditPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            }
          ]
        },
        {
          path: "offices",
          component: SimplePage,
          meta: { servicePermission: "service/master_operation" },
          children: [
            {
              path: "",
              name: "office-list",
              component: () =>
                import(/* webpackChunkName: "office" */
                "./views/office/OfficeListPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: "new",
              name: "office-new",
              component: () =>
                import(/* webpackChunkName: "office" */
                "./views/office/OfficeNewPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":id",
              name: "office-show",
              props: true,
              component: () =>
                import(/* webpackChunkName: "office" */
                "./views/office/OfficeShowPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":id/edit",
              name: "office-edit",
              props: true,
              component: () =>
                import(/* webpackChunkName: "office" */
                "./views/office/OfficeEditPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":companyId/staffs/new",
              name: "office-staff-new",
              props: true,
              component: () =>
                import(/* webpackChunkName: "office" */
                "./views/office/OfficeStaffNewPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":companyId/staffs/:id/edit",
              name: "office-staff-edit",
              props: true,
              component: () =>
                import(/* webpackChunkName: "office" */
                "./views/office/OfficeStaffEditPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":companyId/stamp/new",
              name: "office-stamp-new",
              props: true,
              component: () =>
                import(/* webpackChunkName: "office" */
                "./views/office/OfficeStampNewPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":companyId/stamp/:id/edit",
              name: "office-stamp-edit",
              props: true,
              component: () =>
                import(/* webpackChunkName: "office" */
                "./views/office/OfficeStampEditPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            }
          ]
        },
        {
          path: "staffs",
          component: SimplePage,
          meta: { servicePermission: "service/master_operation" },
          children: [
            {
              path: "",
              name: "staff-list",
              component: () =>
                import(/* webpackChunkName: "staff" */ "./views/staff/StaffListPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: "new",
              name: "staff-new",
              component: () =>
                import(/* webpackChunkName: "staff" */ "./views/staff/StaffNewPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":id/edit",
              name: "staff-edit",
              props: true,
              component: () =>
                import(/* webpackChunkName: "staff" */ "./views/staff/StaffEditPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            }
          ]
        },
        {
          path: "our-staffs",
          component: SimplePage,
          meta: { servicePermission: "service/master_operation" },
          children: [
            {
              path: "",
              name: "our-staff-list",
              component: () =>
                import(/* webpackChunkName: "staff" */ "./views/our-staff/OurStaffListPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: "new",
              name: "our-staff-new",
              component: () =>
                import(/* webpackChunkName: "staff" */ "./views/our-staff/OurStaffNewPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":id/edit",
              name: "our-staff-edit",
              props: true,
              component: () =>
                import(/* webpackChunkName: "staff" */ "./views/our-staff/OurStaffEditPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            }
          ]
        },
        {
          path: "tasktypes",
          component: () =>
            import(/* webpackChunkName: "admin-task-types" */ "./views/TaskTypePage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "minutetypes",
          name: "minute-type",
          component: () =>
            import(/* webpackChunkName: "admin-minute-types" */ "./views/MinuteTypePage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "minutetypes/:questionId/edit",
          name: "minute-type-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "admin-minute-types" */ "./views/MinuteTypeEditPage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "plans",
          name: "plan-list",
          component: () =>
            import(/* webpackChunkName: "plan" */ "./views/PlanListPage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "plans/:id",
          name: "plan-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "plan" */ "./views/PlanEditPage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        // {
        //   path: "schedule-calendar",
        //   name: "schedule-calendar",
        //   component: () =>
        //     import(/* webpackChunkName: "schedule-calendar" */
        //     "./views/ScheduleCalendarPage.vue"),
        //   meta: { servicePermission: "service/master_read" }
        // },
        {
          path: "unit",
          name: "unit-list",
          component: () =>
            import(/* webpackChunkName: "unit" */
            "./ui/accounting/pages/UnitPage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "expense-categories",
          name: "expense-category-list",
          component: () =>
            import(/* webpackChunkName: "expense-category" */
            "./cost/ui/views/ExpenseCategoryListPage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "expense-categories/new",
          name: "expense-category-new",
          component: () =>
            import(/* webpackChunkName: "expense-category" */
            "./cost/ui/views/ExpenseCategoryCreatePage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "expense-categories/:expenseCategoryId",
          name: "expense-category-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "expense-category" */
            "./cost/ui/views/ExpenseCategoryEditPage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "estimate-template",
          name: "estimate-template-list",
          component: () =>
            import(/* webpackChunkName: "estimate-template" */
            "./ui/order/pages/EstimateTemplateListPage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "estimate-template/new",
          name: "estimate-template-new",
          component: () =>
            import(/* webpackChunkName: "estimate-template" */
            "./ui/order/pages/EstimateTemplateCreatePage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "estimate-template/:estimateTemplateId",
          name: "estimate-template-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "estimate-template" */
            "./ui/order/pages/EstimateTemplateEditPage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "service-tasks",
          name: "service-tasks-list",
          component: () =>
            import(/* webpackChunkName: "service-tasks" */
            "./servicetask/ui/pages/ServiceTasksListPage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "service-tasks/new",
          name: "service-tasks-new",
          props: true,
          component: () =>
            import(/* webpackChunkName: "service-tasks" */
            "./servicetask/ui/pages/ServiceTasksCreatePage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "service-tasks/:serviceTaskId",
          name: "service-tasks-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "service-tasks" */
            "./servicetask/ui/pages/ServiceTasksEditPage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "project/tasks",
          name: "project-task",
          component: () =>
            import(/* webpackChunkName: "project-setting-task" */
            "./views/ServiceSettingTask.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "schedule-notification",
          name: "project-task",
          component: () =>
            import(/* webpackChunkName: "schedule-notification" */
            "./views/NotificationConfigScreen.vue"),
          meta: { servicePermission: "service/master_operation" }
        }
      ]
    },
    {
      path: "/admin/project-calendar",
      name: "project-calendar",
      component: () =>
        import(/* webpackChunkName: "project-calendar" */
        "./views/ProjectCalendar.vue"),
      meta: { servicePermission: "service/master_read" }
    },
    {
      path: "/serviceAdmin",
      component: () =>
        import(/* webpackChunkName: "admin" */ "./views/ServiceAdminPage.vue"),
      meta: { servicePermission: "service/master_operation" },
      children: [
        // 他工程
        {
          path: "construction",
          component: SimplePage,
          meta: { servicePermission: "service/master_operation" },
          children: [
            {
              path: "",
              name: "construction-list",
              component: () =>
                import(/* webpackChunkName: "staff" */ "./servicemastar/ui/pages/ConstructionListPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: "new",
              name: "construction-new",
              component: () =>
                import(/* webpackChunkName: "staff" */ "./servicemastar/ui/pages/ConstructionNewPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":id/edit",
              name: "construction-edit",
              props: true,
              component: () =>
                import(/* webpackChunkName: "staff" */ "./servicemastar/ui/pages/ConstructionEditPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            }
          ]
        },
        // 他社工程
        {
          path: "construction-task",
          component: SimplePage,
          meta: { servicePermission: "service/master_operation" },
          children: [
            {
              path: "",
              name: "construction-task-list",
              component: () =>
                import(/* webpackChunkName: "staff" */ "./servicemastar/ui/pages/ConstructionTaskListPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: "new",
              name: "construction-task-new",
              component: () =>
                import(/* webpackChunkName: "staff" */ "./servicemastar/ui/pages/ConstructionTaskNewPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":id/edit",
              name: "construction-task-edit",
              props: true,
              component: () =>
                import(/* webpackChunkName: "staff" */ "./servicemastar/ui/pages/ConstructionTaskEditPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            }
          ]
        },
        // 社内タスク
        {
          path: "other-company-construction",
          component: SimplePage,
          meta: { servicePermission: "service/master_operation" },
          children: [
            {
              path: "",
              name: "other-company-construction-list",
              component: () =>
                import(/* webpackChunkName: "staff" */ "./servicemastar/ui/pages/OtherCompanyConstructionListPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: "new",
              name: "other-company-construction-new",
              component: () =>
                import(/* webpackChunkName: "staff" */ "./servicemastar/ui/pages/OtherCompanyConstructionNewPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            },
            {
              path: ":id/edit",
              name: "other-company-construction-edit",
              props: true,
              component: () =>
                import(/* webpackChunkName: "staff" */ "./servicemastar/ui/pages/OtherCompanyConstructionEditPage.vue"),
              meta: { servicePermission: "service/master_operation" }
            }
          ]
        },
        // セルビスCSVインポート設定
        {
          path: "csv-date",
          name: "csv-date-edit",
          props: true,
          component: () =>
            import(/* webpackChunkName: "admin-minute-types" */ "./servicemastar/ui/pages/CsvDateEditPage.vue"),
          meta: { servicePermission: "service/master_operation" }
        },
        {
          path: "",
          redirect: { name: "construction-list" }
        }
      ]
    },
    {
      path: "/signin",
      name: "sign-in",
      component: () =>
        import(/* webpackChunkName: "sign-in" */ "./views/SignInPage.vue"),
      meta: { noAuth: true }
    },
    {
      path: "/signout",
      name: "sign-out",
      beforeEnter: signOut
    },
    {
      path: "/ReceiveServiceInvitation",
      props: (route: Route) => ({
        inviteCode: route.query.inviteCode
      }),
      component: () =>
        import(/* webpackChunkName: "invitation" */ "./views/ReceiveServiceInvitation.vue")
    },
    {
      path: "/ReceiveProjectInvitation",
      props: (route: Route) => ({
        inviteCode: route.query.inviteCode
      }),
      component: () =>
        import(/* webpackChunkName: "invitation" */ "./views/ReceiveProjectInvitation.vue")
    },
    {
      path: "/lost_password",
      component: () =>
        import(/* webpackChunkName: "reset-password" */ "./views/LostPassword.vue"),
      meta: { noAuth: true }
    },
    {
      path: "/reset/:token",
      props: true,
      component: () =>
        import(/* webpackChunkName: "reset-password" */ "./views/ResetPassword.vue"),
      meta: { noAuth: true }
    },
    {
      path: "/license",
      component: () =>
        import(/* webpackChunkName: "license" */
        "./views/License.vue")
    },
    {
      path: "/serviceTask/:serviceTaskTypeId",
      name: "ServiceSchedulePage",
      props: route => ({
        ...route.params,
        defaultViewMode: route.query.mode
      }),
      // TODO: 印刷用にフォントを食わせてるせいでchunkがクソ重い
      component: () =>
        import(/* webpackChunkName: "service-schedule" */
        "./servicetask/ui/pages/ServiceSchedulePage.vue")
    },
    {
      path: "/403",
      component: () =>
        import(/* webpackChunkName: "403" */
        "./views/403.vue")
    },
    {
      path: "*",
      name: "not-found-page",
      component: () =>
        import(/* webpackChunkName: "not-found" */ "./views/NotFound.vue"),
      meta: { noAuth: true }
    },
    {
      path: "/status",
      name: "status-list",
      component: () =>
        import(/* webpackChunkName: "status-list" */
        "./views/StatusListPage.vue"),
      meta: {
        servicePermission: "service/project_read",
        accessProject: "project/project_access"
      }
    }
  ]
});

router.beforeEach(async (to: Route, _from: Route, next: Function) => {
  if (to.meta.noAuth || store.getters.user.signedIn) {
    if (to.matched.some(record => record.meta.servicePermission)) {
      let permissions = [];
      let projectPermissions = [];
      const servicePermissions = await getServicePermissions();
      const servicePermission = to.meta.servicePermission;
      const projectPermission = to.meta.projectPermission;
      const accessProject = to.meta.accessProject;

      if (to.params.projectId) {
        projectPermissions = await getProjectPermissions(to.params.projectId);
        permissions = [
          ...servicePermissions.map(p => `service/${p.identifier}`),
          ...projectPermissions.map(p => `project/${p.identifier}`)
        ];

        if (accessProject && !permissions.includes(accessProject)) {
          next("/403");
        }
      } else {
        permissions = [
          ...servicePermissions.map(p => `service/${p.identifier}`)
        ];
      }

      if (
        !permissions.includes(servicePermission) &&
        !permissions.includes(projectPermission)
      ) {
        next("/403");
      } else {
        next();
      }
    }
    localStorage.setItem("previousRoute", _from.fullPath);
    next();
  } else {
    // ログインしていなければサインインページに移動
    next({ path: "/signin", query: { destination: to.fullPath } });
  }
});

export default router;
